import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import {
  callApi,
  fnGetDataSet,
  fnSave,
  fnGetTableById,
} from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, fnGetDate } from "@shared/utils/CommonUtils";
import { showPopupModal, showResizablePopupModal } from "@widgets/CommonModal";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import dayjs from "dayjs";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SN_PRD_WRK_REG_001_POP_001 from "@menu/PRD/SN_PRD_WRK_REG_001/SN_PRD_WRK_REG_001_POP_001";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SnRadioBox from "@shared/components/SnRadioBox";
import { ColumnConfig } from "@shared/components/Grid/types";
import {
  buildLabelPrintJobs,
  findLabelPrintError,
  runLabelPrintJobs,
  summarizeLabelPrintJobs,
} from "../PRD_LABEL_PRINT";

type PopupComponentType = React.ComponentType<any>;
const popupContext = (require as any).context(
  "../WRK_REG_POPUP",
  true,
  /^\.\/.*\.tsx$/
);

const POPUP_COMPONENT_MAP: Record<string, PopupComponentType> = (() => {
  const map: Record<string, PopupComponentType> = {};
  popupContext.keys().forEach((key: string) => {
    const mod = popupContext(key);
    const component = mod?.default as PopupComponentType | undefined;
    if (!component) return;
    const relativePath = key.replace(/^\.\//, "").replace(/\.tsx$/i, "");
    const fileName = relativePath.split("/").pop() || relativePath;
    map[relativePath] = component;
    map[relativePath.toUpperCase()] = component;
    map[fileName] = component;
    map[fileName.toUpperCase()] = component;
  });
  return map;
})();
interface Props {
  Menuprops: menuInfo;
}

const SN_PRD_WRK_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const SEARCH_CACHE_KEY = "SN_PRD_WRK_REG_001_SEARCH_CACHE";
  const getSearchCache = () => {
    try {
      const cachedRaw = localStorage.getItem(SEARCH_CACHE_KEY);
      return cachedRaw ? JSON.parse(cachedRaw) : null;
    } catch {
      return null;
    }
  };
  const setSearchCache = (values: any) => {
    try {
      localStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify(values));
    } catch {
      // ignore cache errors
    }
  };
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });
  const searchRef = useRef<any>(null);
  const { userInfo, config } = global();

  const [prd1110, setPRD1110] = useState<string>("");

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const restoredRef = useRef(false);
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [tab, setTab] = useState<string>("1");
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedLineDocu, setSelectedLineDocu] = useState<number>(0);
  const [fgRun, setFgRun] = useState<string>("S");
  const fgRunOptions = useMemo(
    () => [
      { code: "S", name: "정상" },
      { code: "A", name: "재작업" },
    ],
    []
  );

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        const cached = getSearchCache();
        const initialFormData = {
          ...(rtn.formData || {}),
          ...(cached
            ? {
                tp_rout: cached.tp_rout ?? "",
                cd_rout: cached.cd_rout ?? "",
                cd_op: cached.cd_op ?? "",
              }
            : {}),
        };
        setFormData(initialFormData);
        const nextControls = (rtn.formControlData || []).map((control: any) => {
          if (control.cd_param === "cd_rout") {
            return {
              ...control,
              dc_filter: control.dc_filter || "[tp_rout] == '{tp_rout}'",
            };
          }
          if (control.cd_param === "cd_op") {
            return {
              ...control,
              dc_filter: control.dc_filter || "[cd_rout] == '{cd_rout}'",
            };
          }
          return control;
        });
        setFormControlData(nextControls);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
        if (cached) {
          restoredRef.current = true;
        }

        setPRD1110(config?.PRD1110?.dc_ctrl ?? "");
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (restoredRef.current) return;
    if (!formData || formControlData.length === 0) return;
    const cached = getSearchCache();
    if (!cached) return;
    // SearchPanel ref의 setFormdata 미구현 환경에서도 동작하도록
    // data 초기값 병합으로 복원한다.
    setFormData((prev: any) => ({
      ...(prev || {}),
      tp_rout: cached.tp_rout ?? "",
      cd_rout: cached.cd_rout ?? "",
      cd_op: cached.cd_op ?? "",
    }));
    restoredRef.current = true;
  }, [formData, formControlData]);

  // 입출고현황 등에서 수불번호로 드릴다운 — 수불번호는 실적번호(no_run) 또는 실적번호+"_R".
  // 조회에 공정경로·공정·지시기간·지시상태가 필수라 실적 → 작업지시/공정에서 모두 찾아 넣고 바로 조회한다.
  useEffect(() => {
    if ((Menuprops.figure as string) !== "fg_drilldown.Search") return;
    if (columns.grid1.length === 0 || !formData) return;
    const nodeData = Menuprops.nodeData ?? {};
    const no_io = nodeData.no_io;
    if (!no_io) return;
    (async () => {
      const rows: any = await fnGetTableById({
        id: "drilldown_run_wo",
        params: { no_io },
      });
      const wo = Array.isArray(rows) ? rows[0] : undefined;
      if (!wo?.no_wo) {
        await showDialog(
          "alter",
          `수불번호 ${no_io}의 생산실적을 찾을 수 없습니다.`
        );
        return;
      }
      const searchData = {
        ...formData,
        cd_biz: nodeData.cd_biz ?? formData.cd_biz,
        no_wo: wo.no_wo,
        dt_from: wo.dt_wo,
        dt_to: wo.dt_wo,
        st_wo: wo.st_wo === "C" ? "C" : "O",
        tp_rout: wo.tp_wo ?? "",
        cd_rout: wo.cd_rout ?? "",
        cd_op: wo.cd_op ?? "",
        cd_item: "",
        cd_equip: "",
        fg_search: "",
        dc_search: "",
      };
      setFormData(searchData);
      onSearch(searchData);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, Menuprops]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
    onPrint,
  }));

  const onPrint = async () => {
    const rowdata = gridRefs.current["grid2"].current.focusedRowNode().data;
    return rowdata;
  };

  // override: 드릴다운처럼 조회조건 패널 반영(비동기) 전에 바로 조회할 때 넘긴다.
  const onSearch = async (override?: any) => {
    try {
      const searchData = override ?? (searchRef.current?.getFormdata?.() || {});
      const cdRout = (searchData.cd_rout || "").toString().trim();
      const cdOp = (searchData.cd_op || "").toString().trim();

      if (!cdRout) {
        await showDialog(
          "alter",
          "공정경로 및 공정을 선택 후 조회 해주시기 바랍니다."
        );
        return;
      }

      if (!cdOp) {
        await showDialog("alter", "공정을 선택 후 조회 해주시기 바랍니다.");
        return;
      }
      // 조회 성공 플로우 기준으로 최신 선택값을 캐시에 저장
      setSearchCache({
        tp_rout: searchData.tp_rout ?? "",
        cd_rout: searchData.cd_rout ?? "",
        cd_op: searchData.cd_op ?? "",
      });

      const params = {
        id: "sn_prd_wrk_reg_001_01",
        params: searchData,
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      setSelectedDocu("");
      setSelectedLineDocu(0);

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0] || [],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  // 조회조건 변경 시 기존 조회 결과와 선택된 문서 정보를 초기화한다.
  const clearGridDataSources = () => {
    ["grid1", "grid2", "grid3"].forEach((gridId) => {
      gridRefs.current[gridId].current?.resetUpdate?.();
    });
    setDataSources({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    setSelectedDocu("");
    setSelectedLineDocu(0);
  };

  const onSearchPanelValueChanged = (event: ValueChangedEvent) => {
    const { colId } = event;
    if (colId === "tp_rout") {
      clearGridDataSources();
      return {
        cd_rout: "",
        cd_op: "",
      };
    }
    if (colId === "cd_rout") {
      clearGridDataSources();
      return {
        cd_op: "",
      };
    }
    if (colId === "cd_op") {
      clearGridDataSources();
    }
    return {};
  };

  const onFocusedRowChanged = (event: any) => {
    if (!event) {
      return;
    }
    const no_wo = event.no_wo;
    const no_line_wo = event.no_line_wo ?? 0;
    setSelectedDocu("");
    setSelectedLineDocu(0);

    const filterParams: any = { no_wo };
    if (no_line_wo) {
      filterParams.no_line_wo = no_line_wo;
    }
    gridRefs.current["grid2"].current?.applyFilters?.(filterParams);
    gridRefs.current["grid2"].current?.selectAndFocusRow?.(0);
    gridRefs.current["grid3"].current?.applyFilters?.(filterParams);
    gridRefs.current["grid3"].current?.selectAndFocusRow?.(0);
  };

  const onFocusedRunRowChanged = (event: any) => {
    const noRun = (event?.no_run || "").toString();
    setSelectedDocu(noRun);
    setSelectedLineDocu(0);
  };

  const getPopupComponent = (popupCodeRaw: string) => {
    const popupCode = (popupCodeRaw || "")
      .toString()
      .trim()
      .replace(/\\/g, "/")
      .replace(/^\.\/+/i, "")
      .replace(/^WRK_REG_POPUP\//i, "")
      .replace(/\.tsx$/i, "");
    const popupName = popupCode.split("/").pop() || popupCode;
    return {
      popupCode,
      PopupComponent: popupCode
        ? POPUP_COMPONENT_MAP[popupCode] ||
          POPUP_COMPONENT_MAP[popupCode.toUpperCase()] ||
          POPUP_COMPONENT_MAP[popupName] ||
          POPUP_COMPONENT_MAP[popupName.toUpperCase()]
        : undefined,
    };
  };

  const openRunPopup = async (row: any) => {
    const noRun = (row?.no_run || "").toString().trim();
    if (!noRun) return;

    const grid1Ref = gridRefs.current["grid1"].current;
    const focusedNode = grid1Ref?.focusedRowNode?.();
    const focusedData = Array.isArray(focusedNode)
      ? focusedNode[0]
      : focusedNode;
    const selectedRow = focusedData?.data ?? focusedData;

    const { PopupComponent } = getPopupComponent(selectedRow?.cd_popup || "");
    if (!PopupComponent) {
      await showDialog(
        "alter",
        "공정등록에 공정구분이 설정되어있지 않습니다.\n관리자에게 확인하시기 바랍니다."
      );
      return;
    }

    const selectedData = {
      no_run: noRun,
      no_wo: row?.no_wo,
      no_line: row?.no_line_wo ?? selectedRow?.no_line ?? row?.no_line,
      fg_run: row?.fg_run || selectedRow?.fg_run || fgRun,
    };
    const popuptitle = (selectedRow?.nm_op || row?.nm_op || "")
      .toString()
      .trim();
    const component = (
      <PopupComponent selectedData={selectedData} Menuprops={Menuprops} />
    );

    await showResizablePopupModal({
      title: `실적조회(${popuptitle})`,
      width: "1550px",
      height: "900px",
      children: component,
    });
  };

  const onRunNoCellDoubleClicked = async (event: any) => {
    const colId =
      event?.column?.getColId?.() ||
      event?.colDef?.field ||
      event?.colDef?.cd_column ||
      "";
    if (colId !== "no_run") return;
    await openRunPopup(event?.data);
  };

  // 상단 작업지시 그리드 [비가동등록] : 선택된 작업지시 기준 비가동등록 팝업 오픈
  const onGridHBtnsClick = async (id: string) => {
    const selectedRow =
      gridRefs.current["grid1"].current?.focusedRowNode()?.data;
    if (!selectedRow) {
      showDialog("alter", "선택된 행이 없습니다.");
      return;
    }

    switch (id) {
      case "btn_POP1": {
        const selectedData = {
          no_wo: selectedRow.no_wo,
          no_line: selectedRow.no_line,
          cd_item: selectedRow.cd_item,
          nm_item: selectedRow.nm_item,
          qt_wo: selectedRow.qt_wo,
          qt_work: selectedRow.qt_work,
          qt_remain: selectedRow.qt_remain,
          cd_op: selectedRow.cd_op,
          cd_equip: selectedRow.cd_equip,
        };

        const component = (
          <SN_PRD_WRK_REG_001_POP_001 selectedData={selectedData} />
        );
        await showPopupModal({
          title: "비가동등록",
          width: "1550px",
          height: "85%",
          children: component,
          okayBtn: false,
          closeBtnText: "닫기",
        });
        break;
      }
      default:
        break;
    }
  };

  const onAddRow = async () => {
    const grid1Ref = gridRefs.current["grid1"].current;
    const focusedNode = grid1Ref?.focusedRowNode?.();
    const focusedData = Array.isArray(focusedNode)
      ? focusedNode[0]
      : focusedNode;
    const selectedRow = focusedData?.data ?? focusedData;
    if (!selectedRow) return;

    if (selectedRow.st_wo === "C") {
      showDialog("alter", "마감된 작업지시에는 실적을 등록 할 수 없습니다.");
      return;
    }

    if (selectedRow.yn_tm_limit === "Y") {
      if (selectedRow.tm_reg_pre !== null) {
        const now = await fnGetDate();
        const diffSec = dayjs(now).diff(
          dayjs(selectedRow.tm_reg_pre),
          "second"
        );

        if (diffSec < Number(selectedRow.qt_limit)) {
          await showDialog(
            "alter",
            `직전 실적 등록 후 ${selectedRow.qt_limit}초가 지나지 않았습니다. (경과 ${diffSec}초)`
          );
          return;
        }
      }
    }

    if (prd1110 !== "Y") {
      const qtRemain = Number(selectedRow.qt_remain ?? 0) || 0;
      if (qtRemain <= 0 && fgRun === "S") {
        showDialog("alter", "작업 잔량을 확인해 주세요.");
        return;
      }
    }

    // Cache routing selections for next menu open
    const formValues = searchRef.current?.getFormdata?.() || {};
    setSearchCache({
      tp_rout: formValues.tp_rout ?? "",
      cd_rout: formValues.cd_rout ?? "",
      cd_op: formValues.cd_op ?? "",
    });

    const selectedData = {
      no_wo: selectedRow.no_wo,
      no_line: selectedRow.no_line ?? selectedRow.no_line_wo,
      fg_run: fgRun,
    };

    const popuptitle = (selectedRow.nm_op || "").toString().trim();
    const yn_sn_exists = (selectedRow.yn_sn_exists || "").toString().trim();
    const { popupCode, PopupComponent } = getPopupComponent(
      selectedRow.cd_popup || ""
    );

    if (popupCode === "WP3004" && yn_sn_exists === "Y") {
      await showDialog(
        "alter",
        "투입 자재 중 SN 관리 자재가 존재하여 일괄 실적을 등록할 수 없습니다. \n공정구분과 투입자재를 확인해주세요."
      );
      return;
    }

    if (!PopupComponent) {
      await showDialog(
        "alter",
        "공정등록에 공정구분이 설정되어있지 않습니다.\n관리자에게 확인하시기 바랍니다."
      );
      return;
    }

    const component = (
      <PopupComponent selectedData={selectedData} Menuprops={Menuprops} />
    );
    const result = await showPopupModal({
      title: `실적등록(${popuptitle})`,
      width: "1550px",
      height: "95%",
      children: component,
    });
    if (!result || result === "close") return;

    await onSearch();
  };

  const onDeleteRow = async () => {
    if (tab === "1") {
      showDialog(
        "alter",
        "행삭제는 실적 단위로만 가능합니다. 실적 정보 탭을 이용하시기 바랍니다."
      );
      return;
    }

    const grid3Ref = gridRefs.current["grid3"].current;
    const focusedNode = grid3Ref?.focusedRowNode?.();
    const noRun = focusedNode?.data?.no_run;
    const rowIndex = focusedNode?.rowIndex;
    if (rowIndex === undefined || rowIndex === null) return;
    grid3Ref?.deleteRowByIndex?.(rowIndex);

    if (!noRun) return;
    const grid2Ref = gridRefs.current["grid2"].current;
    const deleteIndexes: number[] = [];
    grid2Ref?.forEachNode?.((node: any) => {
      if (node?.data?.no_run === noRun) {
        deleteIndexes.push(node.rowIndex);
      }
    });
    for (let i = deleteIndexes.length - 1; i >= 0; i -= 1) {
      grid2Ref?.deleteRowByIndex?.(deleteIndexes[i]);
    }
  };

  const onSave = async () => {
    try {
      const grid2Changes =
        gridRefs.current["grid2"].current?.getChange?.() || {};
      const grid3Changes =
        gridRefs.current["grid3"].current?.getChange?.() || {};

      const deleteRunl = grid2Changes.delete || [];
      const deleteRunh = grid3Changes.delete || [];

      if (!deleteRunh.length && !deleteRunl.length) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const savePayload = {
        prd_runh: {
          insert: [],
          update: [],
          delete: deleteRunh,
        },
        prd_runl: {
          insert: [],
          update: [],
          delete: deleteRunl,
        },
      };

      await fnSave("SN_PRD_WRK_REG_001_SAVE_01", savePayload);
      gridRefs.current["grid2"].current?.resetUpdate?.();
      gridRefs.current["grid3"].current?.resetUpdate?.();
      return true;
    } catch (error: any) {
      showDialog("error", error.message);
      return false;
    }
  };

  // 상세실적에서 선택한 실적의 라벨을 다시 출력한다.
  // 프린터/출력 서비스 문제로 라벨이 나오지 않은 건을 복구하기 위한 기능이며,
  // 실적 데이터는 이미 저장되어 있으므로 출력만 다시 시도한다.
  const getReprintTargetRows = () => {
    const grid2Ref = gridRefs.current["grid2"].current;
    const selected = grid2Ref?.getSelectedRows?.();
    if (Array.isArray(selected) && selected.length > 0) return selected;

    const focused = grid2Ref?.focusedRowNode?.();
    const focusedData = Array.isArray(focused) ? focused[0] : focused;
    const row = focusedData?.data ?? focusedData;
    return row ? [row] : [];
  };

  const onLabelReprint = async () => {
    const rows = getReprintTargetRows();

    // 양식/출력키가 없으면 사유를 구분해서 안내한다.
    const reprintError = findLabelPrintError(rows);
    if (reprintError) {
      await showDialog("alter", reprintError);
      return;
    }

    // 몇 행을 선택하든 중복은 buildLabelPrintJobs에서 정리한다.
    // 카톤은 같은 no_ct가 한 장으로 합쳐지고, SN은 대상마다 한 장씩 나간다.
    const jobs = buildLabelPrintJobs(rows);
    const { succeeded, failed } = await runLabelPrintJobs(jobs);

    if (failed.length > 0) {
      const failedSummary = summarizeLabelPrintJobs(failed.map((f) => f.job));
      await showDialog(
        "alter",
        `${failedSummary} 출력에 실패했습니다. 프린터와 출력 프로그램 상태를 확인해 주세요.`
      );
      return;
    }

    await showDialog(
      "success",
      `${summarizeLabelPrintJobs(succeeded)}을 출력했습니다.`
    );
  };

  const onBtnsClick = async (id: string) => {
    if (id === "addRow") {
      onAddRow();
    }
    if (id === "deleteRow") {
      onDeleteRow();
    }
    if (id === "btnPrint") {
      await onLabelReprint();
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onRowDataReady: () => {
            gridRefs.current["grid1"].current?.selectAndFocusRow?.(0);
          },
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onFocusedRowChanged: onFocusedRunRowChanged,
          onCellDoubleClicked: onRunNoCellDoubleClicked,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onFocusedRowChanged: onFocusedRunRowChanged,
          onCellDoubleClicked: onRunNoCellDoubleClicked,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchPanelValueChanged}
        />
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "작업지시" }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"비가동등록"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnTab
            titles={["상세실적", "실적정보"]}
            width={"100%"}
            selectedTab={tab}
            onTabChanged={(index: any) => {
              setTab(index);
            }}
            header={[
              <SnTabHeader
                body={
                  <div className="flex items-center">
                    <SnRadioBox
                      defaultValue={fgRun}
                      js_combo={fgRunOptions}
                      onValueChanged={setFgRun}
                    />
                    <SnButton
                      text={"라벨 재출력"}
                      onClick={() => {
                        onBtnsClick("btnPrint");
                      }}
                    />
                  </div>
                }
                onBtnAddClick={onBtnsClick}
                onBtnDeleteClick={onBtnsClick}
              />,
              <SnTabHeader
                onBtnAddClick={onBtnsClick}
                onBtnDeleteClick={onBtnsClick}
              />,
            ]}
            body={[
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                // 라벨 재출력 대상을 여러 건 고를 수 있어야 해서 멀티 선택으로 둔다.
                etcProps={{ rowSelection: "multiple" }}
                events={grids.grid2.events}
              />,
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                etcProps={{ rowSelection: "single" }}
                events={grids.grid3.events}
              />,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={selectedLineDocu}
        />
      </div>
    </div>
  );
});

export default SN_PRD_WRK_REG_001;
